import React, { useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import { VoucherLogWarning } from '~/services/PromoService';

import { humanize } from '~/utils/humanize';
import {
  type VoucherLogWarningSummary,
  getVoucherLogWarningCountsByLevel,
  normalizeVoucherLogWarningLevel,
} from '~/utils/voucherLogWarnings';

type Props = {
  warningSummaries: Array<VoucherLogWarningSummary>;
};

function getWarningLevelChipColor(level: string): ChipProps['color'] {
  switch (normalizeVoucherLogWarningLevel(level)) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    default:
      return 'default';
  }
}

function getWarningLevelLabel(level: string): string {
  return humanize(normalizeVoucherLogWarningLevel(level));
}

export default function VoucherLogWarningsSummaryButton({ warningSummaries }: Props) {
  const warnings = useMemo<Array<VoucherLogWarning>>(
    () => warningSummaries.map(({ warning }) => warning),
    [warningSummaries],
  );
  const levelCounts = useMemo(() => getVoucherLogWarningCountsByLevel(warnings), [warnings]);
  const groupedWarnings = useMemo(() => {
    const summariesByLevel = new Map<string, Array<VoucherLogWarningSummary>>();
    warningSummaries.forEach((summary) => {
      const level = normalizeVoucherLogWarningLevel(summary.warning.level);
      summariesByLevel.set(level, (summariesByLevel.get(level) ?? []).concat(summary));
    });

    return levelCounts.map(({ level, count }) => ({
      level,
      count,
      warnings: summariesByLevel.get(level) ?? [],
      totalsByCode: (summariesByLevel.get(level) ?? []).reduce<Record<string, number>>((acc, summary) => {
        const code = summary.warning.code || 'unknown';
        acc[code] = (acc[code] ?? 0) + 1;
        return acc;
      }, {}),
    }));
  }, [levelCounts, warningSummaries]);

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color={warningSummaries.length > 0 ? 'text.primary' : 'text.secondary'}>
        Warnings
      </Typography>
      {groupedWarnings.length > 0 ? (
        groupedWarnings.map(({ level, count, warnings, totalsByCode }) => (
          <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              variant="outlined"
              color={getWarningLevelChipColor(level)}
              label={`${getWarningLevelLabel(level)}: ${count}`}
            />
            <DebugModal
              type="generic"
              hideIcon
              title={`Voucher Log ${getWarningLevelLabel(level)}s (${count})`}
              data={{
                total: warningSummaries.length,
                totalsByLevel: Object.fromEntries(
                  levelCounts.map(({ level: countLevel, count: levelCount }) => [countLevel, levelCount]),
                ) as Record<string, number>,
                totalsByCode,
                warnings: {
                  [level]: warnings.map(({ warning, ...rest }) => ({
                    ...rest,
                    warning,
                  })),
                },
              }}
              buttonProps={{
                sx: {
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                },
              }}
            >
              View
            </DebugModal>
          </Box>
        ))
      ) : (
        <Chip size="small" variant="outlined" label="0" />
      )}
    </Stack>
  );
}
